import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap, take } from 'rxjs/operators';

import { User } from '../classes/user';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Environment } from '../classes/environment';
import {ToastrService} from 'ngx-toastr';
import {PasswordRequest} from '../classes/PasswordRequest';
import {PasswordReset} from '../classes/passwordReset';
import { CategoryService } from './news-category.service';
import { NewsService } from './news.service';
import { CalendarService } from './calendar.service';
import { ChatPrototypeService } from './chat-prototype.service';
import { MatDialog } from '@angular/material/dialog';
import { InfoDialogComponent } from '@trivelope/triv-ui-tools-lib';
import { NewCountersService } from './new-counters.service';
import { AvatarsService } from './avatars.service';
import { UserService } from './user.service';


export enum AuthResult{ OK, captchaRequired, KO, accountLocked, secondStepNeeded, dataAgreementRequired, userAgreementRequired, twoFaActivationRequired };


@Injectable( { providedIn: 'root' } )
export class AuthenticationService {


	private btoaToken = '';
	private ebeRefreshToken = "";

	private tmpUserId = "";
	private tmpPasswd = "";
	private readonly miniPreloadedIds = new Set<string>();

	bFirstAnimationDone = false;

	timeoutTokenLoop = 0;
	readonly REFRESHTOKEN_INTERVAL_MS = 10 * 60 * 1000;		// every 10 minutes
	// readonly REFRESHTOKEN_INTERVAL_MS = 1 * 60 * 1000;		// every minute to test


	constructor(
		@Inject('environment') private environment: Environment,
		@Inject('LOCALSTORAGE') private localStorage: Storage,
		private httpClient: HttpClient,
		private matSnackBar: MatSnackBar,
    	private toastr: ToastrService,
		private categoryService: CategoryService,
		private calendarService: CalendarService,
		private newsService: NewsService,
		private chatPrototypeService: ChatPrototypeService,
		private matDialog: MatDialog,
		private newCountersService: NewCountersService,
		private avatars: AvatarsService,
		private userService: UserService
	) {}


	/*

		1) 2FA Mandatory and Active: zweiFaktorToken != null  && jwtToken == null && zweiFaktorAktive == true -> Need Validation

		2) 2FA Mandatory and NOT Active: zweiFaktorToken != null && jwtToken == null && zweiFaktorAktive != true -> Need activation

		3) 2FA NOT Mandatory and Active: zweiFaktorToken != null && jwtToken == null && zweiFaktorAktive == true -> Need Validation

		4) 2FA NOT Mandatory and NOT Active: jwtToken != null -> Can go through

	*/

	login( username: string, password: string, dataProtection: boolean, userAgreement: boolean, recaptchaToken: string ): Observable<AuthResult> {

		return this.httpClient.post<{ verarbeitet: boolean, meldung: string, benutzer: User, status: string }>(
			this.environment.backendUrl + 'benutzer/login',
			{
				ebeId: username,
				ebePassword: password,
				ebeDatenschutzBestaetigt: dataProtection,
				ebeNutzungsbedingungBestaetigt: userAgreement,
				recaptchaToken: recaptchaToken
			}
		).pipe(
			map( response => {

				if( !response || !response.benutzer ) return AuthResult.KO;

				if( response.meldung === "Ungueltiger reCAPTCHA-Token" ) return AuthResult.captchaRequired;

				const user = response.benutzer;
				// Speichern des 2FA-Token im localStorage, falls vorhanden
				if( user.zweiFaktorToken ) {
					this.localStorage.setItem('twoFactorToken', user.zweiFaktorToken); // Speichern des Tokens
				}

				const pendingUserForStorage = this.stripProfileImageFields({ ...user, ebeRefreshToken: "" });
				this.localStorage.setItem( 'currentUser', JSON.stringify(pendingUserForStorage));
				this.localStorage.setItem( 'tmsLastLoggedIn', "" + (new Date()).getTime());

				if( !user.ebeDatenschutzBestaetigt ) return AuthResult.dataAgreementRequired;
				if( !user.ebeNutzungsbedingungBestaetigt ) return AuthResult.userAgreementRequired;
				if( !user.jwtToken ) {
					return user.zweiFaktorAktive ? AuthResult.secondStepNeeded : AuthResult.twoFaActivationRequired;
				}
				else {
					user.ebeRefreshToken = user.jwtToken;
				}

				// Benutzer speichern und JWT generieren, nachdem alles abgeschlossen ist
				this.saveUser( user );

				return AuthResult.OK;

			}),
			catchError( err => {

				if (err && err.status === 428 && err.error?.meldung === "Ungueltiger reCAPTCHA-Token") return of(AuthResult.captchaRequired);
				if (err && err.status === 423) return of(AuthResult.accountLocked);
				if (err?.error?.status === "0014") {
					this.tmpUserId = username;
					this.tmpPasswd = password;
					return of(AuthResult.dataAgreementRequired);
				}
				return of(AuthResult.KO);

			})
		);
	}


	loginWithTotp( user: User ): Observable<boolean> {

		console.log('Vorm Aufruf von validate', user);

		this.btoaToken = '';

		return this.httpClient.post<{ verarbeitet: boolean, benutzer: User }>(
			this.environment.backendUrl + 'benutzer/zweifaktor/validate',
			user
		).pipe(
			map( response => {

				console.log('Nach dem Aufruf von Validate');

				if( !response || !response.verarbeitet ) {
					this.matSnackBar.open( "ungültiger Code", '', { duration: 5000 } );
					return false;
				}

				this.saveUser( response.benutzer );
				return true;

			}),
			catchError( err => {
				this.matSnackBar.open( 'Fehler beim Senden des Codes', '', { duration: 5000 } );
				console.log( 'an error occurred while sending the code', err );
				return of( false );
			})
		);

	}

	initializeAppAfterAuth(): Observable<boolean> {

		if( this.environment.chatPrototype ) {
			this.chatPrototypeService.generateProtoData( this.getCurrentUser() );
		}

		const user = this.getCurrentUser();

		return forkJoin([
			this.calendarService.getCalendarGroupsSubscribedToUser( user.ebeId ).pipe(catchError(() => of([]))),
			this.categoryService.loadCategories().pipe(catchError(() => of([]))),
			this.calendarService.loadSubscribedEvents( user.ebeId ).pipe(catchError(() => of([]))),
			this.newsService.getRubricsSubscribedToUser( user.ebeId ).pipe(
				switchMap( rubrics => {
					if( !rubrics ) return of(false);
					this.newsService.storeSubscribedRubrics( rubrics );
					return this.newCountersService.getNewCounters();
				}),
				catchError(() => of(false))
			),

		]).pipe(
			map( responses => {
				// kein Logout mehr bei Teilausfällen
				this.refreshTokenLoop();
				return true;
			})
		);

	}

	logout() {
		this.btoaToken = '';
		this.ebeRefreshToken = "";
		this.miniPreloadedIds.clear();
		this.newCountersService.stopChatPolling();
		if( this.timeoutTokenLoop ) {
			window.clearTimeout( this.timeoutTokenLoop );
			this.timeoutTokenLoop = 0;
		}
		this.localStorage.clear();
	}

	checkPreviousLogin(): boolean {

		const tmsLastLoggedIn = this.localStorage.getItem( "tmsLastLoggedIn" );
		if( !tmsLastLoggedIn ) return false;
		const tmsNow = (new Date()).getTime();
		if( +tmsLastLoggedIn < (tmsNow - 3600000) ) {	// one hour
			this.localStorage.clear();
			return false;
		}

		const strCurrentUser = this.localStorage.getItem( 'currentUser' );
		if( !strCurrentUser ) return false;
		try {

			const user = JSON.parse( strCurrentUser );
			if( !user ) return false;
			if( !user.ebeRefreshToken ) return false;

			this.btoaToken = btoa( user.ebeId + ':' + user.ebeRefreshToken );
			this.ebeRefreshToken = user.ebeRefreshToken;
			if( !user.gruppen ) user.gruppen = [];

			this.localStorage.setItem( 'tmsLastLoggedIn', "" + ( new Date() ).getTime() );
			this.preloadMiniForUserId(user.ebeId);

		}
		catch( e ) {
			return false;
		}

		return true;
	}

	getCurrentUser(): User {

		if( this.environment.offlineMode ) {
			const user = new User();
			user.ebeId = "offlineModeUserID"
			user.ebeName = "Ringer";
			user.ebeVorname = "Richard";
			return user;
		}

		const strCurrentUser = this.localStorage.getItem( 'currentUser' );
		if( !strCurrentUser ) return new User();
		try {
			const user = JSON.parse( strCurrentUser );
			if( user ) {

				this.btoaToken = ( !user.ebeId || !user.ebeRefreshToken )
					? ""
					: btoa( user.ebeId + ':' + user.ebeRefreshToken );

				this.ebeRefreshToken = user.ebeRefreshToken;
				if( this.ebeRefreshToken == null ) this.btoaToken = '';

				if( !user.gruppen ) user.gruppen = [];
				this.localStorage.setItem( 'tmsLastLoggedIn', "" + ( new Date() ).getTime() );
				return user;

			}
		}
		catch( e ) {
			return new User();
		}
		return new User();

	}

	isConnected(): boolean {

		if( this.environment.offlineMode ) return true;

		// console.log( "ISCONNECTED()", !!this.getCurrentUser().ebeId, !!this.btoaToken, !!this.getCurrentUser().ebeId && !!this.btoaToken )
		const b = !!this.getCurrentUser().ebeId && !!this.btoaToken;
		if( b ) {
			this.startLoopRefreshToken();
		}
		return b;

	}

	isAdmin(): boolean {

		if( this.environment.offlineMode && this.environment.offlineModeAdmin ) return true;

		const groups = this.getCurrentUser().gruppen;
		return ( groups && groups.includes( 'ADMIN' ) );

	}

	isAdminMember(): boolean {
		const groups = this.getCurrentUser().gruppen;
		return groups && groups.includes( 'MITGLIED_ADMIN' );
	}

	getBtoaToken(): string {
		return this.btoaToken;

		// const lt = this.localStorage.getItem( 'loginToken' );
		// if( lt ) {
		// 	const o = JSON.parse( lt );
		// 	const tmsNow = (new Date()).getTime();
		// 	if( o.timestamp && (o.timestamp > tmsNow) ) {
		// 		this.loginToken = o.loginToken;
		// 	}
		// }
	}


	changeMyPassword( newPassword: string ): Observable<boolean> {

		const user = this.getCurrentUser();
		return this.changePassword( newPassword, user.ebeId ).pipe(
			map( b => {
				return b;
			})
		);
	}

  forgotPassword(passwordReset: PasswordReset): Observable<boolean> {
    return this.httpClient.post<{ verarbeitet: boolean }>(this.environment.backendUrl + 'passwordForgotten', passwordReset ).pipe(
      map(response => {
        if (response.verarbeitet) {
          this.toastr.success('Link zum Zurücksetzen des Passworts wurde an Ihre E-Mail gesendet');
        }
        return response.verarbeitet;
      }),
      catchError(err => {
        console.log('an error occurred while sending the email', err);
        this.toastr.error('Beim Senden der E-Mail ist ein Fehler aufgetreten');
        return of(false);
      })
    );
  }

  changeForgottenPassword(passwordRequest: PasswordRequest): Observable<boolean> {
    return this.httpClient.post<{ verarbeitet: boolean }>( this.environment.backendUrl + 'passwordReset', passwordRequest
    ).pipe(
      map(response => {
        if (response.verarbeitet) {
          this.toastr.success('Passwort wurde erfolgreich geändert');
        }
        return response.verarbeitet;
      }),
      catchError(err => {
        console.log('an error occurred while ', err);
        this.toastr.error('Beim Zurücksetzen des Passworts ist ein Fehler aufgetreten');
        return of(false);
      })
    );
  }


	changePassword( newPassword: string, userId: string ): Observable<boolean> {

		return this.httpClient.post<{ verarbeitet: boolean, benutzer: User }>(
			this.environment.backendUrl + 'benutzer/password',
			{ ebeId: userId, password: newPassword }
		).pipe(
			map( response => {

				if( !response || !response.verarbeitet ) {

					this.matSnackBar.open(
						"Passwortänderung abgelehnt. Ein sicheres Passwort enthält 12 Zeichen, 1 Zahl, 1 Symbol (!\"#$%&'*+,-./:;=?@[]\^_`|~), , 1 Großbuchstaben und 1 Kleinbuchstaben.",
						'', { duration: 5000 }
					);
					return false;
				}

				this.matSnackBar.open( 'Passwort erfolgreich geändert', '', { duration: 5000 } );
				return true;
			}),
			catchError( err => {
				this.matSnackBar.open( 'Fehler beim Ändern des Passworts', '', { duration: 5000 } );
				console.log( 'an error occurred while changing the password', err );
				return of(false);
			})
		);

	}


	requestTotpEmail( user: User ): Observable<boolean> {

		return this.httpClient.post<{ verarbeitet: boolean, benutzer: User }>(
			this.environment.backendUrl + 'benutzer/zweifaktor/email',
			user
		).pipe(
			map( response => {
				if( !response || !response.verarbeitet ) {
					this.matSnackBar.open( "Die E-Mail-Anfrage ist fehlgeschlagen", '', { duration: 5000 } );
					return false;
				}

				this.matSnackBar.open( 'E-Mail gesendet', '', { duration: 5000 } );
				return true;
			}),
			catchError( err => {
				this.matSnackBar.open( 'Die E-Mail-Anfrage ist fehlgeschlagen', '', { duration: 5000 } );
				console.log( 'The request for an email failed', err );
				return of( false );
			})
		);

	}

	startLoopRefreshToken() {

		if( this.timeoutTokenLoop ) return;	// timeout already in progress

		setTimeout( () => {
			this.refreshTokenLoop();
		}, this.REFRESHTOKEN_INTERVAL_MS );

	}

	private refreshTokenLoop() {

		if( this.timeoutTokenLoop ) return;	// timeout already in progress

		const user = this.getCurrentUser();
		if( !user || !user.ebeId ) return;

		this.timeoutTokenLoop = 1;

		this.httpClient.get<User>(
			this.environment.backendUrl + 'benutzer/token/validate',
			{
				params: new HttpParams()
					.set( 'ebeId', user.ebeId )
					.set( 'jwtToken', this.ebeRefreshToken )
			}
		).subscribe({
			next: userRefreshed => {

				console.log( "benutzer/token/validate", userRefreshed )
				if( userRefreshed && userRefreshed.ebeRefreshToken ) {

					const tokenChanged = userRefreshed.ebeRefreshToken !== this.ebeRefreshToken;
					const currentUser = this.getCurrentUser();
					const mergedUser: User = {
						...currentUser,
						...userRefreshed,
						ebeRefreshToken: userRefreshed.ebeRefreshToken,
					};

					if( !Array.isArray(mergedUser.gruppen) && Array.isArray(currentUser.gruppen) ) {
						mergedUser.gruppen = currentUser.gruppen;
					}

					this.saveUser( mergedUser );

					if( tokenChanged && !mergedUser.mitgliedId ) {
						/*
							fetch the users to know the mitgliedId because the Backend doesn't fill the mitgliedId property anymore (always null after /login)
						*/
						this.userService.getAll().subscribe( users => {
							const myUser = users.find( u => u.ebeId == mergedUser.ebeId );
							if( myUser && myUser.mitgliedId ) {
								const updatedUser: User = { ...mergedUser, mitgliedId: myUser.mitgliedId };
								this.saveUser( updatedUser );
							}
						});
					}

				}

				this.timeoutTokenLoop = window.setTimeout( () => {

					this.timeoutTokenLoop = 0;
					this.refreshTokenLoop();

				}, this.REFRESHTOKEN_INTERVAL_MS );

			},
			error: err => {
				console.log( 'benutzer/token/validate error', err );
				this.timeoutTokenLoop = window.setTimeout( () => {
					this.timeoutTokenLoop = 0;
					this.refreshTokenLoop();
				}, this.REFRESHTOKEN_INTERVAL_MS );
			}
		});

	}


	private saveUser( user: User ) {
		const sanitizedUser = this.stripProfileImageFields(user);
		this.localStorage.setItem( 'currentUser', JSON.stringify( sanitizedUser ) );
		this.btoaToken = btoa( user.ebeId + ':' + user.ebeRefreshToken );
		this.ebeRefreshToken = user.ebeRefreshToken;
		this.preloadMiniForUserId(user.ebeId);

	}

	private stripProfileImageFields<T extends Record<string, any>>(user: T | null | undefined): T | null | undefined {
		if (!user) return user;
		const sanitized: any = { ...user };
		delete sanitized.profilbild;
		delete sanitized.profilBild;
		delete sanitized.ebeProfilbild;
		delete sanitized.ebeProfilbildMini;
		delete sanitized.profilbildMini;
		delete sanitized.avatarMini;
		delete sanitized.ebeProfilbildMime;
		delete sanitized.profilbildMime;
		delete sanitized.avatarMime;
		return sanitized;
	}

	private preloadMiniForUserId(ebeId: string | null | undefined): void {
		if (!ebeId) return;
		if (this.miniPreloadedIds.has(ebeId)) return;
		this.miniPreloadedIds.add(ebeId);
		this.avatars.ensureMinis([ebeId]).pipe(take(1)).subscribe();
	}



	setTmpCredentials(username: string, password: string): void {
		this.tmpUserId = username;
		this.tmpPasswd = password;
	}

	public getTmpUserId(): string {
		return this.tmpUserId;
	}

	public getTmpPassword(): string {
		return this.tmpPasswd;
	}

	hasRole() {
		const groups = this.getCurrentUser().gruppen;
		return ( groups && ( groups.includes( 'MITGLIED_ADMIN' ) ||
			groups.includes( 'MITGLIED_DATENPFLEGE' )));
	}

}


